import React, { useEffect } from "react";
import { PhoneMissed, PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { PanelModal, PanelModalContent, PanelModalHeaderActions, panelModalSizeClasses, usePanelModalExpand } from "@/components/ui/panel-modal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const statusLabel = (durumAciklama, durum) => {
  if (durumAciklama === "cevaplanan") return "Cevaplandı";
  if (durumAciklama === "mesgul") return "Meşgul";
  return "Cevapsız";
};

const UnfollowedMissedModal = ({
  isOpen,
  onClose,
  data,
  loading,
  isDark,
  textClass,
  labelClass,
}) => {
  const { expanded, setExpanded, toggleExpanded } = usePanelModalExpand(true);

  useEffect(() => {
    if (isOpen) setExpanded(true);
  }, [isOpen, setExpanded]);

  if (!isOpen) return null;

  const records = data?.records || [];
  const isSingleDay = data?.start_date === data?.end_date;

  return (
    <PanelModal open onClose={onClose}>
      <PanelModalContent
        className={`w-full my-2 sm:my-4 rounded-xl shadow-2xl flex flex-col ${panelModalSizeClasses(expanded, "lg")} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`sticky top-0 z-10 flex items-center justify-between gap-2 p-3 sm:p-4 border-b rounded-t-xl ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"}`}>
          <div className="min-w-0">
            <h3 className={`text-sm sm:text-lg font-semibold truncate ${textClass}`}>
              <PhoneMissed className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2 text-rose-500" />
              Takipsiz Cevapsızlar
            </h3>
            {data?.agent_name && (
              <p className={`text-xs mt-0.5 truncate ${labelClass}`}>
                {data.agent_name}
                {data.start_date && (
                  <> · {isSingleDay ? data.start_date : `${data.start_date} – ${data.end_date}`}</>
                )}
              </p>
            )}
          </div>
          <PanelModalHeaderActions
            expanded={expanded}
            onToggle={toggleExpanded}
            onClose={onClose}
            isDark={isDark}
          />
        </div>

        <div className={`px-3 sm:px-4 py-2 text-xs border-b ${isDark ? "border-slate-800 bg-slate-800/30 text-slate-400" : "border-gray-100 bg-gray-50 text-gray-600"}`}>
          O gün yalnızca bir kez temas edilen ve cevapsız kalan numaralar. Tekrar arama veya müşteri geri dönüşü varsa listede yer almaz.
        </div>

        <div className="p-3 sm:p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="spinner w-8 h-8" />
            </div>
          ) : records.length === 0 ? (
            <div className={`text-center py-16 ${labelClass}`}>
              <PhoneMissed className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Takipsiz cevapsız kayıt bulunamadı</p>
            </div>
          ) : (
            <div className={`overflow-x-auto rounded-xl border ${isDark ? "border-slate-700" : "border-gray-200"}`}>
              <Table className="text-xs sm:text-sm">
                <TableHeader>
                  <TableRow className={isDark ? "border-slate-700 bg-slate-800/80" : "bg-rose-50/80 border-rose-100"}>
                    {!isSingleDay && <TableHead className={isDark ? "text-slate-400" : ""}>Tarih</TableHead>}
                    <TableHead className={isDark ? "text-slate-400" : ""}>Saat</TableHead>
                    <TableHead className={isDark ? "text-slate-400" : ""}>Yön</TableHead>
                    <TableHead className={isDark ? "text-slate-400" : ""}>Numara</TableHead>
                    <TableHead className={isDark ? "text-slate-400" : ""}>Arayan</TableHead>
                    <TableHead className={isDark ? "text-slate-400" : ""}>Aranan</TableHead>
                    <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((row) => (
                    <TableRow key={`${row.id}-${row.tarih}`} className={isDark ? "border-slate-700/50 hover:bg-slate-800/40" : "hover:bg-rose-50/50"}>
                      {!isSingleDay && (
                        <TableCell className={`font-mono whitespace-nowrap ${isDark ? "text-slate-300" : ""}`}>
                          {row.call_date}
                        </TableCell>
                      )}
                      <TableCell className={`font-mono whitespace-nowrap ${isDark ? "text-slate-300" : ""}`}>
                        {row.call_time || "-"}
                      </TableCell>
                      <TableCell>
                        {row.yon === 2 ? (
                          <Badge className={isDark ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-blue-100 text-blue-700 border-blue-200"}>
                            <PhoneOutgoing className="w-3 h-3 mr-1" />Giden
                          </Badge>
                        ) : (
                          <Badge className={isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
                            <PhoneIncoming className="w-3 h-3 mr-1" />Gelen
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={`font-mono font-semibold ${isDark ? "text-rose-300" : "text-rose-700"}`}>
                        {row.phone}
                      </TableCell>
                      <TableCell className={`font-mono text-[10px] sm:text-xs ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                        {row.arayan_numara || "-"}
                      </TableCell>
                      <TableCell className={`font-mono text-[10px] sm:text-xs ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                        {row.aranan_numara || "-"}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs font-medium ${isDark ? "text-amber-400" : "text-amber-700"}`}>
                          {statusLabel(row.durum_aciklama, row.durum)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {!loading && data && (
          <div className={`px-4 py-2 text-xs border-t ${isDark ? "border-slate-800 text-slate-500" : "border-gray-100 text-gray-500"}`}>
            Toplam: <strong className={isDark ? "text-rose-300" : "text-rose-700"}>{data.total ?? records.length}</strong> numara
          </div>
        )}
      </PanelModalContent>
    </PanelModal>
  );
};

export default UnfollowedMissedModal;
